import { useCallback, useEffect, type Dispatch, type SetStateAction } from 'react';
import { api } from '@/lib/api';
import type { Message } from '@/types';
import { mergeMessageLists } from '@/utils/mergeMessages';
import { useConversationRoom, useWebSocket } from './useWebSocket';

interface PaginatedMessages {
  data: Message[];
}

const POLL_MS = 2500;

/**
 * Keep an open conversation thread current.
 *
 * WhatsApp inbound and agent replies are written by agent-orchestrator and
 * never emit `message:new` on staff-api, so the open thread also polls.
 * Staff-sent messages still arrive over the shared Socket.IO room.
 */
export function useOpenThreadSync(
  conversationId: string | null,
  setMessages: Dispatch<SetStateAction<Message[]>>,
): void {
  useConversationRoom(conversationId);

  const applyIncoming = useCallback(
    (incoming: Message[]) => {
      if (incoming.length === 0) {
        return;
      }
      setMessages((prev) => mergeMessageLists(prev, incoming));
    },
    [setMessages],
  );

  useWebSocket(
    'message:new',
    useCallback(
      (message: Message) => {
        if (!conversationId || message.conversationId !== conversationId) {
          return;
        }
        applyIncoming([message]);
      },
      [applyIncoming, conversationId],
    ),
    { enabled: Boolean(conversationId) },
  );

  useWebSocket(
    'message:status',
    useCallback(
      (data: { messageId: string; status: Message['status'] }) => {
        setMessages((prev) =>
          prev.map((message) =>
            message.id === data.messageId ? { ...message, status: data.status } : message,
          ),
        );
      },
      [setMessages],
    ),
    { enabled: Boolean(conversationId) },
  );

  useEffect(() => {
    if (!conversationId) {
      return;
    }

    let cancelled = false;

    const refresh = async () => {
      if (document.hidden) {
        return;
      }
      try {
        const response = await api.get<PaginatedMessages>(
          `/conversations/${conversationId}/messages`,
        );
        if (cancelled) {
          return;
        }
        applyIncoming(response.data);
      } catch {
        // Keep the thread that is already on screen.
      }
    };

    const intervalId = window.setInterval(() => {
      void refresh();
    }, POLL_MS);
    const onVisibility = () => {
      if (!document.hidden) {
        void refresh();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [applyIncoming, conversationId]);
}
